/* global debounce */

if (!customElements.get('predictive-search')) {
  class PredictiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.input = this.querySelector('.js-search-input');
      this.productTypeSelect = this.querySelector('.js-search-product-types');
      this.productTypeInput = document.getElementById('product_type_input');
      this.resetBtn = this.querySelector('.js-search-reset');
      this.results = this.querySelector('.js-search-results');
      
      // Create overlay if it doesn't exist
      this.overlay = this.querySelector('.js-search-overlay');
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'js-search-overlay predictive-search-overlay';
        this.appendChild(this.overlay);
      }
      
      this.statusEl = this.querySelector('.js-search-status');
      this.loadingText = this.getAttribute('data-loading-text');
      this.addListeners();
    }

    /**
     * Triggers when the web component is removed from the DOM
     */
    disconnectedCallback() {
      this.close();
    }

    addListeners() {
      this.input.addEventListener('focus', this.handleFocus.bind(this));
      this.input.addEventListener('input', debounce(this.handleInput.bind(this)));
      if (this.productTypeSelect) {
        this.productTypeSelect.addEventListener('change', this.handleProductTypeChange.bind(this));
      }
      this.querySelector('.search').addEventListener('submit', this.handleSubmit.bind(this));
    }

    /**
     * Gets the value of the search input field.
     * @returns {string}
     */
    getQuery() {
      return this.input.value.trim();
    }

    /**
     * Handles search form submit.
     * @param {object} evt - Event object.
     */
    handleSubmit(evt) {
      if (
        !this.getQuery().length
        || this.querySelector('.predictive-search__item[aria-selected="true"]:not(.js-submit)')
      ) {
        evt.preventDefault();
      }
    }

    /**
     * Handles 'input' events on the search field.
     */
    handleInput() {
      // autocomplete=off prevents browser from restoring value after using back button in Safari
      this.input.setAttribute('value', this.input.value);

      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.close();
        return;
      }

      this.getResults(searchTerm);
    }

    /**
     * Handles a change of product type
     * @param {object} evt - Event object.
     */
    handleProductTypeChange(evt) {
      this.productTypeInput.value = evt.detail.selectedValue;
      const query = this.getQuery();
      if (query.length > 0) {
        this.getResults(query);
      }
    }

    /**
     * Handles 'focus' events on the search field.
     */
    handleFocus() {
      // Start loading the product name list as soon as the field is focused, so
      // it is usually ready by the first search; a failed load is retried then.
      this.loadNameIndex().catch(() => {});

      const searchTerm = this.getQuery();
      if (!searchTerm.length) return;

      if (this.getAttribute('results') === 'true') {
        this.open();
      } else {
        this.getResults(searchTerm);
      }
    }

    /**
     * Handles 'keydown' events on the custom element.
     * @param {object} evt - Event object.
     */
    handleKeydown(evt) {
      // Let tabs script handle keydown events on the tab buttons.
      if (evt.target.matches('.tablist__tab')) return;

      switch (evt.key) {
        case 'ArrowUp':
        case 'ArrowDown':
          evt.preventDefault();

          if (this.hasAttribute('open')) {
            this.handleResultsNav(evt.key);
          }
          break;

        case 'Enter':
          this.selectOption();
          break;

        case 'Escape':
          this.close();
          break;

        // no default
      }
    }

    /**
     * Handles 'keyup' events on the custom element.
     */
    handleKeyup() {
      // If search field is empty after key press, close the results.
      if (!this.getQuery().length) this.close();
    }

    /**
     * Handles keyboard navigation of the results.
     * @param {string} key - Key pressed.
     */
    handleResultsNav(key) {
      const selectedResult = this.querySelector(
        '[role="tabpanel"]:not([hidden]) [aria-selected="true"]'
      );
      const allResults = this.querySelectorAll(
        '[role="tabpanel"]:not([hidden]) .predictive-search__item'
      );
      let resultToSelect = allResults[0];

      if (key === 'ArrowUp') {
        if (!selectedResult) return;

        // Select the next result up or the last one if we've reached the top of the list.
        resultToSelect = selectedResult.previousElementSibling || allResults[allResults.length - 1];
      } else if (key === 'ArrowDown') {
        if (selectedResult) {
          // Select the next result down or the first one if we've reached the bottom of the list.
          resultToSelect = selectedResult.nextElementSibling || allResults[0];
        }
      }

      this.statusEl.textContent = '';

      if (resultToSelect) {
        // If the selected item didn't change, do nothing.
        if (resultToSelect === selectedResult) return;

        resultToSelect.setAttribute('aria-selected', 'true');
        if (selectedResult) selectedResult.setAttribute('aria-selected', 'false');

        this.setLiveRegionText(resultToSelect.textContent);
        this.input.setAttribute('aria-activedescendant', resultToSelect.id);
      }
    }

    /**
     * Selects a result
     */
    selectOption() {
      const selectedResult = this.querySelector('[aria-selected="true"] > .js-search-link');
      if (selectedResult) selectedResult.click();
    }

    /**
     * Gets the results for a search term.
     * @param {string} searchTerm - Search query.
     */
    async getResults(searchTerm) {
      // Only the newest search may render: a slower, older response that lands
      // after it (typing "pap" then "paprika") would otherwise replace it.
      this.latestRequest = (this.latestRequest || 0) + 1;
      const requestId = this.latestRequest;
      this.setLiveRegionLoadingState();

      // Theme settings > Search > "Rank dropdown by title matches and best
      // sellers" off: list the search page's own first results instead. Only an
      // explicit false switches: layouts that do not pass the setting keep the
      // default ranking.
      if (theme.settings.pSearchCustomRanking === false) {
        await this.getSearchPageResults(requestId);
        return;
      }

  let searchFields = 'title,product_type,variants.title,vendor';
      if (theme.settings.pSearchIncludeSkus) searchFields += ',variants.sku';
      if (theme.settings.pSearchIncludeTags) searchFields += ',tag';

      // What is sent to Shopify:
      //   one word    -> the word as typed           Shopify's own ranking, typos included
      //   two words   -> (a b) OR (ab) OR (a) OR (b*) also each word alone, for
      //                  products that carry only one of them ("sweet almond")
      //   more words  -> (a b c) OR (abc)             the phrase, plus the joined-up spelling
      // Shopify's ranking is meaning-based and can put a product whose name holds
      // the typed text below ten looser matches ("pap" -> Peppermint, Lavender),
      // so a title lookup runs alongside and mergeTitleMatches() moves name
      // matches to the top. No one-word OR group: Shopify now ranks those the
      // same fuzzy way, and splitting a word into many fragments ("pa prika",
      // "pap rika", ...) made it return nothing at all.
      // A leading "-" is dropped from each word: Shopify reads "-word" as "not
      // word". Hyphens inside a word ("tea-tree") are kept.
      const cleaned = (searchTerm || '')
        .toLowerCase()
        .replace(/[():"*]/g, ' ')
        .split(/\s+/)
        .map((word) => word.replace(/^-+/, ''))
        .filter(Boolean)
        .join(' ');
      if (!cleaned) {
        this.close();
        return;
      }
      const words = cleaned.split(' ');
      let orGroup = cleaned;
      if (words.length === 2) {
        orGroup = `(${cleaned}) OR (${words.join('')}) OR (${words[0]}) OR (${words[1]}*)`;
      } else if (words.length > 2) {
        orGroup = `(${cleaned}) OR (${words.join('')})`;
      }
      const typeFilter =
        this.productTypeInput && this.productTypeInput.value !== ''
          ? `product_type:${encodeURIComponent(this.productTypeInput.value)} AND `
          : '';

      let sharedParams = `&${encodeURIComponent('resources[limit]')}=${theme.settings.pSearchLimit}`;
      sharedParams += `&${encodeURIComponent('resources[limit_scope]')}=${theme.settings.pSearchLimitScope}`;
      sharedParams += `&${encodeURIComponent('resources[options][fields]')}=${searchFields}`;
      sharedParams += '&section_id=predictive-search';

      const searchParams = `q=${typeFilter}${encodeURIComponent(orGroup)}${sharedParams}`;
      // The longest typed word is also looked up in product titles alone; the
      // merge keeps the titles that contain every typed word ("paprika oleo").
      const titleWord = words.reduce((longest, word) => (word.length > longest.length ? word : longest), '');
      const titleParams =
        titleWord.length >= 3 ? `q=${typeFilter}${encodeURIComponent(`title:${titleWord}*`)}${sharedParams}` : '';

      const queryKey = searchParams.replace(' ', '-').toLowerCase();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      const fetchSection = async (params) => {
        const response = await fetch(`${theme.routes.predictiveSearch}?${params}`);
        if (!response.ok) throw new Error(response.status);
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        return tmpl.content.querySelector('#shopify-section-predictive-search');
      };

      // Product names are matched on this device too (see loadNameIndex), so a
      // product whose name starts with what was typed is listed first even when
      // Shopify leaves it out of its ten results. The first search of a visit
      // waits at most a moment for the list; after that it is instant. A product
      // type filter narrows Shopify's results, and the list carries no type, so
      // the name matches are left out while one is chosen.
      const nameMatchesPromise = typeFilter
        ? Promise.resolve(null)
        : Promise.race([
            this.loadNameIndex().then(() => this.findNameMatches(cleaned)),
            new Promise((resolve) => {
              setTimeout(() => resolve(null), 1200);
            })
          ]).catch(() => null);

      try {
        const [resultsEl, titleEl, nameMatches] = await Promise.all([
          fetchSection(searchParams),
          titleParams ? fetchSection(titleParams).catch(() => null) : null,
          nameMatchesPromise
        ]);

        const merged = this.mergeTitleMatches(resultsEl, titleEl, cleaned, nameMatches || []);
        const resultsMarkup = merged.innerHTML.replace(/psearch/g, this.input.id);

        // Results missing the name matches (list still loading) are not cached,
        // so the same search shows them once the list has arrived.
        if (nameMatches || typeFilter) this.cachedResults[queryKey] = resultsMarkup;
        if (requestId !== this.latestRequest) return;
        this.renderResults(resultsMarkup);

        // The list was not ready in time (a first search typed straight away):
        // run this search once more when it arrives, unless the shopper has
        // typed something else since, so best sellers still come first.
        if (!nameMatches && !typeFilter) {
          this.loadNameIndex()
            .then(() => {
              if (requestId === this.latestRequest && this.getQuery() === searchTerm) this.getResults(searchTerm);
            })
            .catch(() => {});
        }
      } catch (error) {
        if (requestId !== this.latestRequest) return;
        this.close();
        throw error;
      }
    }

    /**
     * Gets the first results of the search page itself, in its order (Shopify
     * relevance). The request is built from the search form's own fields and
     * action, so it is exactly the search the form submits, and the rows match
     * the search page one for one. sections/predictive-search-relevance.liquid
     * renders them.
     * @param {number} requestId - Id of this search, from getResults.
     */
    async getSearchPageResults(requestId) {
      const form = this.querySelector('.search');
      const params = new URLSearchParams(new FormData(form));
      params.set('section_id', 'predictive-search-relevance');

      const queryKey = params.toString();
      if (this.cachedResults[queryKey]) {
        this.renderResults(this.cachedResults[queryKey]);
        return;
      }

      // Each keystroke supersedes the last: cancel its request rather than let
      // it finish unused.
      if (this.searchPageController) this.searchPageController.abort();
      this.searchPageController = new AbortController();

      try {
        const response = await fetch(`${form.getAttribute('action')}?${queryKey}`, {
          signal: this.searchPageController.signal,
        });
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();
        const section = tmpl.content.querySelector('#shopify-section-predictive-search-relevance');
        if (!section) throw new Error('Search request returned no predictive-search-relevance section');

        const resultsMarkup = section.innerHTML.replace(/psearch/g, this.input.id);
        this.cachedResults[queryKey] = resultsMarkup;
        if (requestId !== this.latestRequest) return;
        this.renderResults(resultsMarkup);
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== this.latestRequest) return;
        // The search page still works from the form, so a failed dropdown just
        // closes; the error is logged for debugging, not thrown unhandled.
        this.close();
        console.error(error);
      }
    }

    /**
     * Orders products by how well their title matches what was typed: the
     * whole term first, then the most typed words, keeping Shopify's order
     * within each tier. Adds title matches the main query missed.
     * @param {Element} resultsEl - Section markup from the main query.
     * @param {?Element} titleEl - Section markup from the title-only query.
     * @param {string} term - Cleaned, lower-case search term.
     * @param {Array<object>} [nameMatches] - Products from the name index.
     * @returns {Element} The section markup to render.
     */
    mergeTitleMatches(resultsEl, titleEl, term, nameMatches = []) {
      const listSelector = '#psearch-products .predictive-search__list';
      const itemSelector = '.predictive-search__item:not(.js-submit)';
      const words = term.split(' ');
      const titleOf = (item) => (item.querySelector('.predictive-result__title')?.textContent || '').toLowerCase();
      const matches = (item) => words.every((word) => titleOf(item).includes(word));
      const score = (item) => {
        const title = titleOf(item);
        return (title.includes(term) ? 100 : 0) + words.filter((word) => title.includes(word)).length;
      };
      // Each query tags its links with its own tracking params, so products are
      // matched on the URL path alone.
      const linkOf = (item) => (item.querySelector('a')?.getAttribute('href') || '').split('?')[0];

      const titleItems = titleEl
        ? Array.from(titleEl.querySelectorAll(`${listSelector} > ${itemSelector}`)).filter(matches)
        : [];

      let base = resultsEl;
      let list = base.querySelector(listSelector);
      if (!list && titleItems.length && titleEl) {
        base = titleEl;
        list = base.querySelector(listSelector);
      }
      if (!list) return base;

      const mainItems = Array.from(list.querySelectorAll(`:scope > ${itemSelector}`));
      // Name matches come first, best seller first (findNameMatches keeps the
      // list's best-selling order). One Shopify also returned reuses Shopify's
      // row; the ones it missed get a row built from the list.
      const mainPaths = mainItems.map((item) => linkOf(item));
      const nameItems = nameMatches.map((product) => {
        const found = mainPaths.indexOf(product.u);
        return found === -1 ? this.buildNameResult(product) : mainItems[found];
      });
      const limit = Math.max(mainItems.length, Number(theme.settings.pSearchLimit) || mainItems.length);
      const ordered = [];
      const seen = new Set();
      [...nameItems, ...titleItems, ...mainItems].forEach((item) => {
        const key = linkOf(item);
        if (seen.has(key)) return;
        seen.add(key);
        ordered.push(item);
      });
      // Array sort is stable, so equal scores keep the order above: name
      // matches by best sellers, then Shopify's relevance order.
      ordered.sort((a, b) => score(b) - score(a));
      ordered.length = Math.min(ordered.length, limit);

      const tail = list.querySelector(':scope > .js-submit');
      mainItems.forEach((item) => item.remove());
      ordered.forEach((item) => list.insertBefore(item, tail));
      return base;
    }

    /**
     * Loads the product name index once per visit: every product's title, url,
     * image, SKUs and barcodes from templates/collection.search-index.liquid,
     * 250 products per page, in best-selling order (Shopify's own sales
     * ranking), so matches can be listed best seller first. Kept in
     * sessionStorage so later pages reuse it.
     * @returns {Promise<Array<object>>}
     */
    loadNameIndex() {
      if (PredictiveSearch.nameIndex) return PredictiveSearch.nameIndex;

      // Versioned: a list stored before it was sorted by best sellers is ignored.
      const storageKey = 'predictive-search-name-index-v2';
      try {
        const stored = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
        if (stored && Date.now() - stored.savedAt < 60 * 60 * 1000) {
          PredictiveSearch.nameIndex = Promise.resolve(stored.products);
          return PredictiveSearch.nameIndex;
        }
      } catch (error) {
        // Storage can be unavailable (private mode); load it over the network.
      }

      const pageUrl = (page) => `${theme.routes.allProducts}?view=search-index&sort_by=best-selling&page=${page}`;
      const fetchPage = async (page) => {
        const response = await fetch(pageUrl(page));
        if (!response.ok) throw new Error(response.status);
        return response.json();
      };

      PredictiveSearch.nameIndex = fetchPage(1)
        .then(async (first) => {
          const rest = [];
          for (let page = 2; page <= first.pages; page += 1) rest.push(fetchPage(page));
          const pages = [first, ...(await Promise.all(rest))];
          const products = pages.flatMap((result) => result.products);
          try {
            sessionStorage.setItem(storageKey, JSON.stringify({ savedAt: Date.now(), products }));
          } catch (error) {
            // Full or unavailable storage only costs a reload of the list later.
          }
          return products;
        })
        .catch((error) => {
          // A failed load is retried on the next search rather than cached.
          PredictiveSearch.nameIndex = null;
          throw error;
        });

      return PredictiveSearch.nameIndex;
    }

    /**
     * Products whose name has a word starting with each typed word, or, for a
     * single typed word, whose SKU or barcode starts with it, as many as the
     * results show. A name starting with the whole term ranks first, then a
     * first word that matches, then any other match ("2 oz PET Bottle" for
     * "pe"); within each, best seller first (the order of the list).
     * @param {string} term - Cleaned, lower-case search term.
     * @returns {Promise<Array<object>>} Up to the predictive search limit.
     */
    async findNameMatches(term) {
      const products = await this.loadNameIndex();
      const words = term.split(' ');
      const matched = [];

      products.forEach((product) => {
        const title = product.t.toLowerCase();
        const nameWords = title.split(/[^a-z0-9]+/).filter(Boolean);
        const byName = words.every((word) => nameWords.some((nameWord) => nameWord.startsWith(word)));
        const byCode =
          words.length === 1 &&
          [...product.s, ...product.b].some((code) => String(code).toLowerCase().startsWith(term));
        if (!byName && !byCode) return;

        let rank = 1;
        if (title.startsWith(term)) rank = 3;
        else if (nameWords[0] && nameWords[0].startsWith(words[0])) rank = 2;
        matched.push({ product, rank });
      });

      // Array sort is stable, so each rank keeps the list's best-selling order.
      return matched
        .sort((a, b) => b.rank - a.rank)
        .slice(0, Number(theme.settings.pSearchLimit) || 10)
        .map((match) => match.product);
    }

    /**
     * Builds a results row for a name-index product in the markup of
     * snippets/predictive-search-tab-panel.liquid.
     * @param {object} product - Name-index entry.
     * @returns {Element}
     */
    buildNameResult(product) {
      const item = document.createElement('li');
      item.className = 'predictive-search__item';
      item.setAttribute('role', 'option');

      const link = document.createElement('a');
      link.className = 'predictive-result flex items-start focus-inset js-search-link';
      link.href = product.u;

      const media = document.createElement('div');
      media.className = 'predictive-result__media media relative';
      if (product.i) {
        const image = document.createElement('img');
        image.className = 'predictive-result__media';
        image.src = product.i;
        image.alt = '';
        image.width = 112;
        image.height = 112;
        image.loading = 'eager';
        media.appendChild(image);
      }

      const info = document.createElement('div');
      info.className = 'predictive-result__info flex-auto';
      const title = document.createElement('h3');
      title.className = 'predictive-result__title h6';
      title.textContent = product.t;
      info.appendChild(title);

      link.append(media, info);
      item.appendChild(link);
      return item;
    }

    /**
     * Sets the live region loading state.
     */
    setLiveRegionLoadingState() {
      this.setLiveRegionText(this.loadingText);
    }

    /**
     * Sets the live region text.
     * @param {string} statusText - Status text.
     */
    setLiveRegionText(statusText) {
      this.statusEl.setAttribute('aria-hidden', 'false');
      this.statusEl.textContent = statusText;

      setTimeout(() => {
        this.statusEl.setAttribute('aria-hidden', 'true');
      }, 1000);
    }

    /**
     * Renders the results markup.
     * @param {string} resultsMarkup - Results markup.
     */
    renderResults(resultsMarkup) {
      // Create a temporary element to parse the markup
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = resultsMarkup;

      // Ensure tabpanels are visible when there's a search query
      if (this.getQuery().length > 0) {
        const tabpanels = tempDiv.querySelectorAll('.predictive-search__tabpanel');
        tabpanels.forEach(panel => {
          panel.removeAttribute('hidden');
        });
      }

      // Products stay in the order Shopify returns them (relevance), so the
      // closest match is listed first rather than the first alphabetically.

      // Create a custom event with the raw results data
      const rawResults = {
        markup: tempDiv.innerHTML,
        products: this.cachedResults[Object.keys(this.cachedResults).pop()]
      };
      
      // Dispatch event before rendering
      const preRenderEvent = new CustomEvent('predictive-search:pre-render', {
        detail: rawResults,
        bubbles: true
      });
      this.dispatchEvent(preRenderEvent);

      // If the event wasn't prevented, render normally
      if (!preRenderEvent.defaultPrevented) {
        this.results.innerHTML = tempDiv.innerHTML;

        // Ensure tabpanels remain visible after rendering
        if (this.getQuery().length > 0) {
          const tabpanels = this.results.querySelectorAll('.predictive-search__tabpanel');
          tabpanels.forEach(panel => {
            panel.removeAttribute('hidden');
          });
        }
      }
      
      this.setAttribute('results', true);
      this.open();

      // Dispatch post-render event
      const postRenderEvent = new CustomEvent('predictive-search:post-render', {
        detail: {
          container: this.results,
          products: this.cachedResults[Object.keys(this.cachedResults).pop()]
        },
        bubbles: true
      });
      this.dispatchEvent(postRenderEvent);
    }

    /**
     * Opens the results listbox.
     */
    open() {
      this.overlay.classList.add('is-visible');
      if (this.getQuery().length) this.resetBtn.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.setAttribute('open', '');
      document.body.classList.add('overlay-predictive-search');

      // Add event handlers (so the bound event listeners can be removed).
      this.keydownHandler = this.keydownHandler || this.handleKeydown.bind(this);
      this.keyupHandler = this.keyupHandler || this.handleKeyup.bind(this);
      this.resetBtnClickHandler = this.resetBtnClickHandler || this.close.bind(this);
      this.overlayClickHandler = this.overlayClickHandler || this.close.bind(this);
      // A press anywhere outside the search box and its results closes them.
      // The overlay element was meant to catch this, but it is unstyled and has
      // no size, so a click outside landed on the page and the panel stayed open
      // with the page behind it locked.
      this.outsidePressHandler =
        this.outsidePressHandler ||
        ((evt) => {
          if (!this.contains(evt.target)) this.close();
        });

      // Add event listeners (for while results are open).
      this.addEventListener('keydown', this.keydownHandler);
      this.addEventListener('keyup', this.keyupHandler);
      this.resetBtn.addEventListener('click', this.resetBtnClickHandler);
      this.overlay.addEventListener('click', this.overlayClickHandler);
      document.addEventListener('pointerdown', this.outsidePressHandler);
    }

    /**
     * Closes the results listbox.
     * @param {object} evt - Event object.
     */
    close(evt) {
      // Any search still loading is now out of date; it must not reopen the panel.
      this.latestRequest = (this.latestRequest || 0) + 1;

      // If reset button was clicked, empty the input field.
      if (evt && evt.target === this.resetBtn) {
        this.input.value = '';
        this.removeAttribute('results');
        this.input.focus();
      }

      // Deselect the selected result (if there is one).
      const selected = this.querySelector('.predictive-search__item[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');

      this.removeAttribute('open');
      this.resetBtn.hidden = true;
      this.overlay.classList.remove('is-visible');
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overlay-predictive-search');

      // Remove event listeners added on search results opening.
      this.removeEventListener('keydown', this.keydownHandler);
      this.resetBtn.removeEventListener('click', this.resetBtnClickHandler);
      this.overlay.removeEventListener('click', this.overlayClickHandler);
      document.removeEventListener('pointerdown', this.outsidePressHandler);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
